import { AlertCircle, QrCode } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useEmployeeManualOrderPaymentQrcode } from '@/hooks/useEmployeeManualOrder'

interface ManualOrderPaymentDialogProps {
  orderId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ManualOrderPaymentDialog({ orderId, open, onOpenChange }: ManualOrderPaymentDialogProps) {
  const { data, isLoading, isError, error } = useEmployeeManualOrderPaymentQrcode(
    orderId ?? 0,
    open && orderId !== null
  )
  const hasQrcode = data?.payment_qrcode_status === 'available' && data.payment_qrcode
  const fallbackText = data?.payment_qrcode_status === 'no_department'
    ? '当前员工未分配部门，暂无收款码'
    : data?.payment_department_name
      ? `顶级部门「${data.payment_department_name}」未设置收款码，请到结算设置配置`
      : '暂无可用收款码，请到结算设置配置'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>线下收款</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="space-y-4">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-36 w-36" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error ? error.message : '收款信息加载失败，请稍后重试'}
            </AlertDescription>
          </Alert>
        )}

        {data && (
          <div className="space-y-5">
            <div className="rounded-md border bg-muted/30 p-4">
              <div className="text-sm text-muted-foreground">订单号</div>
              <div className="font-mono text-sm font-medium">{data.order_id}</div>
              <div className="mt-3 text-sm text-muted-foreground">收款金额</div>
              <div className="text-2xl font-bold text-red-600">¥{data.pay_price.toFixed(2)}</div>
            </div>

            {hasQrcode ? (
              <div className="flex flex-col items-center gap-3">
                <div className="rounded-md border bg-white p-3">
                  <img
                    src={data.payment_qrcode}
                    alt={`${data.payment_department_name || '部门'}收款码`}
                    className="h-48 w-48 object-contain"
                  />
                </div>
                {data.payment_department_name && (
                  <div className="text-sm text-muted-foreground">
                    收款部门：{data.payment_department_name}
                  </div>
                )}
              </div>
            ) : (
              <Alert variant="warning">
                <QrCode className="h-4 w-4" />
                <AlertDescription>{fallbackText}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-2 text-sm">
              <span className="text-muted-foreground">收货人</span>
              <span>{data.real_name || '-'} {data.user_phone || ''}</span>
              <span className="text-muted-foreground">商品</span>
              <span>{data.product_summary || '暂无商品信息'}</span>
              <span className="text-muted-foreground">数量</span>
              <span>{data.total_num} 件</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
